import * as React from 'react';
import { Id } from '../../utils/ids.js';
import { Bem } from '../../utils/css.js';
import { createChildIsTypeOf } from '../../utils/component-util.js';
import { getKeyboardFocusableElements, warnOnce, omitKeys } from '../../utils/common.js';
import { HeaderMenuGroup } from './HeaderMenuGroup.js';
import { HeaderMenuItem } from './HeaderMenuItem.js';
import './HeaderMenu.scss.js';
import { HeaderItem } from './HeaderItem.js';
import { Key } from '../../constants.js';

const bem = Bem.create("vai-header-menu");
function findNextElementWithClass(element, findClassName) {
  if (!element) {
    return null;
  }
  let foundElement = null;
  while (element) {
    if (element.classList.contains(findClassName)) {
      foundElement = element;
      break;
    }
    element = element.nextElementSibling;
  }
  return foundElement;
}
const elementContainsEventTarget = (element, target) => {
  if (!element || !(target instanceof Element)) {
    return false;
  }
  return !!element.contains(target);
};
const findNewFocusTarget = (key, target, dropDownRefCurrent, cssClasses) => {
  let newFocusTarget = null;
  switch (key) {
    case Key.ArrowDown:
      const nextSibling = target.nextElementSibling ?? dropDownRefCurrent.firstChild;
      newFocusTarget = findNextElementWithClass(nextSibling, cssClasses.focusClass);
      break;
    case Key.ArrowUp:
      const previousSibling = target.previousElementSibling ?? dropDownRefCurrent.lastChild;
      newFocusTarget = findNextElementWithClass(previousSibling, cssClasses.focusClass);
      break;
    case Key.Home:
      newFocusTarget = dropDownRefCurrent.firstChild;
      break;
    case Key.End:
      newFocusTarget = dropDownRefCurrent.lastChild;
      break;
  }
  return newFocusTarget;
};
const _HeaderMenu = class extends React.Component {
  constructor() {
    super(...arguments);
    this.state = { open: !!this.props.openOnMount, children: [] };
    this.dropDownRef = React.createRef();
    this.menuButtonRef = React.createRef();
    this.handleOutsideClicks = (event) => {
      if (!elementContainsEventTarget(this.menuButtonRef.current, event.target) && !elementContainsEventTarget(this.dropDownRef.current, event.target)) {
        this.setState({ open: false });
        this.removeOutsideClickEventHandlers();
      }
    };
    this.removeOutsideClickEventHandlers = () => {
      document.removeEventListener("mousedown", this.handleOutsideClicks, { capture: true });
    };
    this.handleDropDownKeyDown = (event) => {
      const { target } = event;
      if (!target || !(target instanceof HTMLElement)) {
        return;
      }
      const { current } = this.dropDownRef;
      const lastItemClass = "vai-header-menu-last-item";
      const firstItemClass = "vai-header-menu-first-item";
      const focusElementClass = "vai-focusable";
      const preventIfClass = (className) => {
        if (!target.classList.contains(className)) {
          return;
        }
        this.handleClose();
        event.preventDefault();
      };
      const capturedKeys = [Key.Home, Key.Escape, Key.End, Key.ArrowDown, Key.ArrowUp, Key.Tab];
      if (current === null || !capturedKeys.includes(event.key)) {
        return;
      }
      if (event.shiftKey && event.key === Key.Tab) {
        preventIfClass(firstItemClass);
        return;
      }
      if (event.key === Key.Escape) {
        this.handleClose();
        return;
      }
      if (event.key === Key.Tab) {
        preventIfClass(lastItemClass);
      }
      const childrenArray = getKeyboardFocusableElements(current);
      if (childrenArray === null || childrenArray.length === 0) {
        return;
      }
      childrenArray[childrenArray.length - 1].classList.add(lastItemClass);
      childrenArray[0].classList.add(firstItemClass);
      childrenArray.forEach((child) => {
        child.classList.add(focusElementClass);
      });
      const newFocusTarget = findNewFocusTarget(event.key, target, current, { focusClass: focusElementClass, lastItemClass });
      if (newFocusTarget !== null) {
        newFocusTarget.focus();
        event.preventDefault();
      }
    };
    this.handleClose = () => {
      if (this.menuButtonRef.current) {
        this.menuButtonRef.current.focus();
      }
      this.setState({ open: false });
      this.removeOutsideClickEventHandlers();
    };
    this.handleMenuButtonKeyDown = (event) => {
      if ([" ", Key.Enter, Key.ArrowDown].includes(event.key)) {
        const { open } = this.state;
        if (Key.ArrowDown === event.key && open === true) {
          return;
        }
        this.setState({ open: !open });
        if (this.dropDownRef.current) {
          const firstFocusableItem = getKeyboardFocusableElements(this.dropDownRef.current);
          if (firstFocusableItem !== null && firstFocusableItem[0] !== void 0) {
            setTimeout(() => {
              firstFocusableItem[0].focus();
            }, 200);
          } else {
            warnOnce('Could not move keyboard focus to the first focusable element because there was none.Use HeaderMenuItem component with "a", "button" or eg. react-router Link.');
          }
        }
      }
    };
    this.handleToggle = () => {
      const { open } = this.state;
      this.setState({ open: !open });
      if (open) {
        this.removeOutsideClickEventHandlers();
      }
    };
  }
  static getDerivedStateFromProps(props) {
    const childrenArray = React.Children.toArray(props.children);
    const dropdownItems = childrenArray.reduce(_HeaderMenu.collectDropdownItems, []);
    const childHasIcon = dropdownItems.some((child) => !!child.props.iconName);
    return { children: childHasIcon ? _HeaderMenu.addEmptyIconPropToDropdownItemWithoutIcon(childrenArray) : props.children };
  }
  static collectDropdownItems(acc, child) {
    if (_HeaderMenu.childIsDropdownItem(child)) {
      return [...acc, child];
    } else if (_HeaderMenu.childIsDropDownGroup(child)) {
      return React.Children.toArray(child.props.children).reduce(_HeaderMenu.collectDropdownItems, acc);
    }
    return acc;
  }
  static addEmptyIconPropToDropdownItemWithoutIcon(children) {
    return children.map((child) => {
      if (_HeaderMenu.childIsDropdownItem(child)) {
        if (!child.props.iconName) {
          return React.cloneElement(child, { iconName: "empty" });
        }
      } else if (_HeaderMenu.childIsDropDownGroup(child)) {
        return React.cloneElement(child, void 0, _HeaderMenu.addEmptyIconPropToDropdownItemWithoutIcon(React.Children.toArray(child.props.children)));
      }
      return child;
    });
  }
  render() {
    const { children, open } = this.state;
    const omitProps = ["openOnMount"];
    const filteredProps = omitKeys(this.props, omitProps);
    const { title, iconName, iconColor, htmlId, ...rest } = filteredProps;
    const id = Id.create(htmlId, ["header dropdown"]);
    return /* @__PURE__ */ React.createElement("div", {
      id: id.toString(),
      "data-ta": this.props.dataTa,
      className: bem.block()
    }, /* @__PURE__ */ React.createElement(HeaderItem, {
      innerRef: this.menuButtonRef,
      component: "button",
      className: bem.element("button")(),
      iconColor,
      iconName,
      title,
      isOpen: open,
      onMouseDown: this.handleToggle,
      onKeyDown: this.handleMenuButtonKeyDown,
      "aria-haspopup": "true",
      "aria-expanded": open ? "true" : "false",
      ...rest
    }), children && /* @__PURE__ */ React.createElement("div", {
      id: id.copyWith(["content"]).toString(),
      className: bem.element("children")(open ? "open" : "closed"),
      onClick: this.handleClose,
      onKeyDown: this.handleDropDownKeyDown,
      role: "menu",
      ref: this.dropDownRef
    }, children));
  }
  componentDidMount() {
    if (this.props.openOnMount) {
      document.addEventListener("mousedown", this.handleOutsideClicks, { capture: true });
    }
  }
  componentDidUpdate() {
    if (!this.state.open) {
      return;
    }
    document.addEventListener("mousedown", this.handleOutsideClicks, { capture: true });
  }
  componentWillUnmount() {
    this.removeOutsideClickEventHandlers();
  }
};
let HeaderMenu = _HeaderMenu;
HeaderMenu.defaultProps = {
  openOnMount: false
};
HeaderMenu.childIsDropDownGroup = createChildIsTypeOf(HeaderMenuGroup);
HeaderMenu.childIsDropdownItem = createChildIsTypeOf(HeaderMenuItem);

export { HeaderMenu };
